import { useCallback, useEffect, useState } from "react";
import { api, type FxChange, type FxRate } from "../lib/api.ts";
import { currentLang, dbLabel, setLang, t, type Lang } from "../i18n/lang.ts";
import { num } from "../lib/format.ts";
import { CURRENCY, UI } from "../i18n/dict.ts";

/**
 * Оболочка интерфейса: боковое меню, верхняя панель, заголовок экрана.
 *
 * Вынесена из App.tsx при переходе на фирменный дизайн 10.09.2026. Причина не
 * в размере файла: оболочка — единственная часть, где у дизайна ДРУГАЯ
 * структура, а не другой цвет. Экраны получили новый вид через тему, без правок
 * разметки и, значит, без риска для расчётов; здесь же меняется сама вёрстка,
 * и держать её отдельно от логики экранов честнее.
 */

/* ------------------------------------------------------------------ иконки
 *
 * Контурные иконки в 24 сетке, как в дизайн-канвасе (там подключён Lucide).
 * Отдельную библиотеку не тянем: нужно двенадцать штук, и это меньше, чем один
 * её импорт. Обводка наследует цвет, поэтому иконка живёт в обеих темах.
 */

const ICON: Record<string, string> = {
  digest: "M8 2v4M16 2v4M3 10h18M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2zM8 14h3M8 18h6",
  overview: "M3 3h7v9H3zM14 3h7v5h-7zM14 12h7v9h-7zM3 16h7v5H3z",
  parks: "M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z",
  brands: "m12 2 9 5-9 5-9-5 9-5ZM3 12l9 5 9-5M3 17l9 5 9-5",
  pipeline: "m16 3 4 4-4 4M20 7H4m8 14-4-4 4-4M4 17h16",
  deals: "M2 8h20v10a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2zM6 8V5a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v3M12 15h.01",
  marketing: "m3 11 18-5v12L3 14zM11.6 16.8a3 3 0 1 1-5.8-1.6",
  sales: "M12 20V10M18 20V4M6 20v-4",
  /* Портфель: экран B2B — школы и компании. */
  b2b: "M3 7h18v13H3zM8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M3 13h18M11 13v2h2v-2",
  journey: "M9 19h8.5a3.5 3.5 0 0 0 0-7h-11a3.5 3.5 0 0 1 0-7H15",
  clients: "M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M13 7a4 4 0 1 1-8 0 4 4 0 0 1 8 0M22 21v-2a4 4 0 0 0-3-3.87",
  metrics:
    "M12 7v14M3 18a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h5a4 4 0 0 1 4 4 4 4 0 0 1 4-4h5a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1h-6a3 3 0 0 0-3 3 3 3 0 0 0-3-3z",
  export: "M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3",
  admin: "M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-2.82 1.17V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 7.26 19.4l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 3.09 14H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 8.4l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 10 4.6V3a2 2 0 0 1 4 0v1.6a1.65 1.65 0 0 0 2.82 1.17l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 20.91 10H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z",
  calendar: "M8 2v4M16 2v4M3 10h18M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z",
  tight: "M3 6h18M3 10h18M3 14h18M3 18h18",
  cozy: "M3 7h18M3 12h18M3 17h18",
  palette: "M12 22a10 10 0 1 1 0-20c5.5 0 10 3.6 10 8 0 3.3-2.7 6-6 6h-1.8a1.7 1.7 0 0 0-1.2 2.9A1.7 1.7 0 0 1 12 22z",
  theme: "M12 8a2.83 2.83 0 0 0 4 4 4 4 0 1 1-4-4M12 2v2M12 20v2m4.9-17.8-1.4 1.4M6.5 17.5l-1.4 1.4M2 12h2M20 12h2m-2.9-7.8-1.4 1.4M6.5 6.5 5.1 5.1",
  print: "M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2M6 9V3h12v6M6 14h12v8H6z",
  collapse: "m11 17-5-5 5-5M18 17l-5-5 5-5",
  close: "M18 6 6 18M6 6l12 12",
};

export function Icon({
  name,
  size = 17,
  width = 2.2,
}: {
  name: keyof typeof ICON | string;
  size?: number;
  width?: number;
}): React.ReactElement {
  const extra = name === "parks" || name === "clients" || name === "journey";
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={width}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{ flex: "none" }}
    >
      <path d={ICON[name] ?? ICON["overview"]} />
      {/* У трёх иконок есть кружок, который в одну кривую не уложить. */}
      {name === "parks" && <circle cx="12" cy="10" r="3" />}
      {name === "clients" && <circle cx="9" cy="7" r="4" />}
      {name === "journey" && (
        <>
          <circle cx="6" cy="19" r="3" />
          <circle cx="18" cy="5" r="3" />
        </>
      )}
      {name === "admin" && <circle cx="12" cy="12" r="3" />}
      {!extra && name === "deals" && <circle cx="12" cy="13" r="2.4" />}
    </svg>
  );
}

/* -------------------------------------------------------------- оформление
 *
 * Тема, плотность и свёрнутое меню живут на `<html>`, а не внутри React-дерева:
 * панель со списком лидов рисуется поверх страницы, а правила печати смотрят на
 * корень документа. Держать это в одном месте дешевле, чем протаскивать флаги.
 *
 * Выбор запоминается в localStorage. Чтение обёрнуто в try: в приватном окне
 * и при запрете куки обращение к хранилищу выбрасывает исключение, и без
 * обёртки приложение не отрисовалось бы вовсе.
 */

type Theme = "dark" | "light";
type Density = "tight" | "normal" | "cozy";

/**
 * Шрифты текста на выбор.
 *
 * Заказчик сформулировал так: фирменный шрифт красив, но не лучший по
 * читаемости, и человек должен иметь выбор. Меняется ТОЛЬКО текстовый шрифт —
 * заголовки и крупные числа остаются фирменными, иначе интерфейс перестаёт быть
 * интерфейсом Zamania и становится безымянным дашбордом.
 *
 * Отбор не по красоте. Здесь только гуманистические и нейтральные гротески с
 * различимыми `I`, `l` и `1` и с настоящими табличными цифрами: в таблице на
 * девять колонок цифра, которая шатается по ширине, стоит дороже любой
 * характерности. Geometric-гротески вроде Poppins и Montserrat не взяты
 * намеренно — у них `I` и `l` неразличимы, а это цена, которую в отчёте платить
 * нечем.
 *
 * `query` — параметры Google Fonts. Начертания перечислены точно теми, что у
 * семейства есть: неверная ось возвращает 400 на весь запрос, и шрифт не
 * загрузился бы вовсе. У Lato и Atkinson нет 500 и 600, поэтому их и нет в
 * списке.
 */
export type FontChoice = {
  id: string;
  label: string;
  note: string;
  stack: string;
  query: string | null;
};

export const FONTS: FontChoice[] = [
  {
    id: "brand",
    label: "Alegreya Sans",
    note: "the brand face · loaded already",
    stack: '"Alegreya Sans", "Trebuchet MS", system-ui, sans-serif',
    query: null,
  },
  {
    id: "atkinson",
    label: "Atkinson Hyperlegible",
    note: "designed for low vision · the most legible here",
    stack: '"Atkinson Hyperlegible", system-ui, sans-serif',
    query: "Atkinson+Hyperlegible:wght@400;700",
  },
  {
    id: "inter",
    label: "Inter",
    note: "the interface workhorse · tight and neutral",
    stack: '"Inter", system-ui, sans-serif',
    query: "Inter:wght@400;500;600;700",
  },
  {
    id: "ibm-plex",
    label: "IBM Plex Sans",
    note: "made for data · unmistakable 1, I and l",
    stack: '"IBM Plex Sans", system-ui, sans-serif',
    query: "IBM+Plex+Sans:wght@400;500;600;700",
  },
  {
    id: "source",
    label: "Source Sans 3",
    note: "wide apertures · easy at small sizes",
    stack: '"Source Sans 3", system-ui, sans-serif',
    query: "Source+Sans+3:wght@400;500;600;700",
  },
  {
    id: "public",
    label: "Public Sans",
    note: "built for public services · plain on purpose",
    stack: '"Public Sans", system-ui, sans-serif',
    query: "Public+Sans:wght@400;500;600;700",
  },
  {
    id: "noto",
    label: "Noto Sans",
    note: "the broadest coverage · Arabic names render",
    stack: '"Noto Sans", system-ui, sans-serif',
    query: "Noto+Sans:wght@400;500;600;700",
  },
  {
    id: "nunito",
    label: "Nunito Sans",
    note: "rounded terminals · closest in mood to the brand",
    stack: '"Nunito Sans", system-ui, sans-serif',
    query: "Nunito+Sans:wght@400;500;600;700",
  },
  {
    id: "manrope",
    label: "Manrope",
    note: "modern and even · slightly narrower rows",
    stack: '"Manrope", system-ui, sans-serif',
    query: "Manrope:wght@400;500;600;700",
  },
  {
    id: "lato",
    label: "Lato",
    note: "warm humanist · long captions read calmly",
    stack: '"Lato", system-ui, sans-serif',
    query: "Lato:wght@400;700",
  },
];

/**
 * Шрифт подгружается ТОЛЬКО когда его выбрали.
 *
 * Десять семейств в `index.html` — это десять запросов и лишние сотни килобайт
 * у каждого, кто ничего не менял. Ссылка добавляется один раз на семейство и
 * остаётся: снимать её при возврате к фирменному значило бы качать заново при
 * следующем переключении.
 */
function ensureFont(font: FontChoice): void {
  if (font.query === null || typeof document === "undefined") return;
  const id = `font-${font.id}`;
  if (document.getElementById(id) !== null) return;
  const link = document.createElement("link");
  link.id = id;
  link.rel = "stylesheet";
  link.href = `https://fonts.googleapis.com/css2?family=${font.query}&display=swap`;
  document.head.appendChild(link);
}

function stored(key: string, fallback: string): string {
  try {
    return window.localStorage.getItem(key) ?? fallback;
  } catch {
    return fallback;
  }
}

function remember(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* приватное окно — просто не запоминаем */
  }
}

export type Chrome = {
  theme: Theme;
  density: Density;
  mini: boolean;
  font: string;
  /**
   * Раскрыта ли панель фильтров. ДЕЙСТВУЕТ ТОЛЬКО НА УЗКОМ ЭКРАНЕ.
   *
   * На телефоне шапка с периодом, парками и сохранёнными видами занимала до
   * трети экрана и оставалась на месте при прокрутке — заказчик на это и
   * пожаловался. Признак живёт здесь, рядом с `mini`, а не в App: это
   * предпочтение оформления, оно должно переживать перезагрузку и храниться там
   * же, где тема и плотность, — иначе у одного и того же понятия появится второй
   * механизм.
   *
   * Скрытие делает CSS внутри `@media (max-width: 900px)`, а не `hidden` на
   * элементе: правило `[hidden]` спрятало бы фильтры и на широком экране, где
   * кнопки-переключателя нет вовсе, и вернуть их было бы нечем.
   */
  filtersOpen: boolean;
  toggleTheme: () => void;
  setTheme: (t: Theme) => void;
  setDensity: (d: Density) => void;
  setFont: (id: string) => void;
  toggleMini: () => void;
  toggleFilters: () => void;
};

export function useChrome(): Chrome {
  const [theme, setTheme] = useState<Theme>(() =>
    stored("zm.theme", "dark") === "light" ? "light" : "dark",
  );
  const [density, setDensityState] = useState<Density>(() => {
    const v = stored("zm.density", "normal");
    return v === "tight" || v === "cozy" ? v : "normal";
  });
  const [mini, setMini] = useState(() => stored("zm.side", "wide") === "mini");
  /* По умолчанию СВЁРНУТА: на широком экране признак ничего не меняет (правило
     стоит под медиа-запросом), а на узком чинит ровно то, на что пожаловались,
     без единого нажатия. */
  const [filtersOpen, setFiltersOpen] = useState(() => stored("zm.filters", "closed") === "open");
  const [font, setFontState] = useState(() => {
    const id = stored("zm.font", "brand");
    return FONTS.some((f) => f.id === id) ? id : "brand";
  });

  useEffect(() => {
    const root = document.documentElement;
    root.dataset["theme"] = theme;
    if (density === "normal") delete root.dataset["density"];
    else root.dataset["density"] = density;
    if (mini) root.dataset["side"] = "mini";
    else delete root.dataset["side"];
    if (filtersOpen) delete root.dataset["filters"];
    else root.dataset["filters"] = "hidden";
  }, [theme, density, mini, filtersOpen]);

  /**
   * Шрифт подменяет ТОЛЬКО текстовую переменную. `--f-display` не трогается:
   * заголовки и крупные числа — фирменный голос системы, и менять его вместе с
   * текстом значило бы стирать бренд ради читаемости подписей.
   */
  useEffect(() => {
    const choice = FONTS.find((f) => f.id === font) ?? FONTS[0]!;
    ensureFont(choice);
    if (choice.id === "brand") document.documentElement.style.removeProperty("--f-body");
    else document.documentElement.style.setProperty("--f-body", choice.stack);
  }, [font]);

  const toggleTheme = useCallback(() => {
    setTheme((t) => {
      const next = t === "dark" ? "light" : "dark";
      remember("zm.theme", next);
      return next;
    });
  }, []);

  const setDensity = useCallback((d: Density) => {
    setDensityState(d);
    remember("zm.density", d);
  }, []);

  const setThemeExact = useCallback((t: Theme) => {
    setTheme(t);
    remember("zm.theme", t);
  }, []);

  const setFont = useCallback((id: string) => {
    setFontState(id);
    remember("zm.font", id);
  }, []);

  const toggleMini = useCallback(() => {
    setMini((m) => {
      remember("zm.side", m ? "wide" : "mini");
      return !m;
    });
  }, []);

  const toggleFilters = useCallback(() => {
    setFiltersOpen((o) => {
      remember("zm.filters", o ? "closed" : "open");
      return !o;
    });
  }, []);

  return {
    theme,
    density,
    mini,
    font,
    filtersOpen,
    toggleTheme,
    setTheme: setThemeExact,
    setDensity,
    setFont,
    toggleMini,
    toggleFilters,
  };
}

/**
 * Счётчик смены оформления для графиков.
 *
 * Графики echarts собираются в `useEffect` и цвета читают из CSS-переменных.
 * Сами по себе они не узнают, что тему переключили, — и остались бы тёмными на
 * светлой странице. Наблюдатель следит за атрибутами корня документа и меняет
 * число, которое графики держат в зависимостях: одна строка вместо протаскивания
 * темы через все экраны.
 */
export function useThemeTick(): number {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const obs = new MutationObserver(() => setTick((t) => t + 1));
    obs.observe(document.documentElement, {
      attributes: true,
      // Язык здесь же, а не отдельным наблюдателем: подписи осей и подсказки
      // графиков тоже надо перерисовать, а два механизма для одного означали
      // бы, что однажды поправят только один.
      attributeFilter: ["data-theme", "data-density", "data-lang"],
    });
    return () => obs.disconnect();
  }, []);
  return tick;
}

/* ------------------------------------------------------------ оформление
 *
 * Всё, чем человек настраивает вид, собрано в одном месте по требованию
 * заказчика. До этого тема, плотность и печать стояли тремя отдельными
 * кнопками в панели фильтров и спорили за место с самими фильтрами, хотя
 * относятся не к данным, а к тому, как на них смотрят.
 */

export function AppearanceMenu({
  chrome,
  isAdmin = false,
}: {
  chrome: Chrome;
  isAdmin?: boolean;
}): React.ReactElement {
  const [open, setOpen] = useState(false);

  /* Закрытие по щелчку вне и по Escape. Без первого панель остаётся открытой,
     когда человек ушёл работать с фильтрами, и перекрывает их собой. */
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent): void => {
      const t = e.target;
      if (t instanceof Element && t.closest("[data-appearance]") !== null) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("mousedown", onDown);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onDown);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div data-appearance className="appearance">
      <button
        type="button"
        className="icon-btn"
        aria-expanded={open}
        aria-haspopup="dialog"
        title={t(UI.appearance)}
        aria-label={t(UI.appearance)}
        onClick={() => setOpen((o) => !o)}
      >
        <Icon name="palette" size={16} />
      </button>

      {open && (
        <div className="appearance-panel" role="dialog" aria-label={t(UI.appearance)}>
          <div className="appearance-head">
            <b>{t(UI.appearance)}</b>
            {/*
              Оговорка «только этот браузер» относится к оформлению, а не ко
              всей панели: курс валюты ниже — общий для всех. Держать одну
              подпись на две разные по смыслу группы значило бы соврать про
              одну из них.
            */}
            <span className="sub">{t(UI.appearanceScope)}</span>
          </div>

          {/*
            Язык стоит первым. Он меняет и саму панель, поэтому человек сразу
            видит результат выбора на том тексте, который читает.
          */}
          <div className="appearance-group">
            <span className="filter-label">{t(UI.language)}</span>
            <div className="seg">
              {(["en", "ru"] as const).map((l) => (
                <button
                  key={l}
                  type="button"
                  aria-selected={currentLang() === l}
                  onClick={() => setLang(l)}
                >
                  {l === "en" ? "English" : "Русский"}
                </button>
              ))}
            </div>
            <span className="sub">{t(UI.languageScope)}</span>
          </div>

          <div className="appearance-group">
            <span className="filter-label">{t(UI.theme)}</span>
            <div className="seg">
              <button
                type="button"
                aria-selected={chrome.theme === "dark"}
                onClick={() => chrome.setTheme("dark")}
              >
                {t(UI.dark)}
              </button>
              <button
                type="button"
                aria-selected={chrome.theme === "light"}
                onClick={() => chrome.setTheme("light")}
              >
                {t(UI.light)}
              </button>
            </div>
          </div>

          <div className="appearance-group">
            <span className="filter-label">{t(UI.density)}</span>
            <div className="seg">
              {(["tight", "normal", "cozy"] as const).map((d) => (
                <button
                  key={d}
                  type="button"
                  aria-selected={chrome.density === d}
                  onClick={() => chrome.setDensity(d)}
                >
                  {d === "tight" ? t(UI.tight) : d === "cozy" ? t(UI.cozy) : t(UI.normal)}
                </button>
              ))}
            </div>
          </div>

          <div className="appearance-group">
            <span className="filter-label">{t(UI.textFont)}</span>
            {/*
              Меняется только текстовый шрифт. Сказано прямо, потому что иначе
              человек выберет шрифт, увидит фирменные заголовки прежними и решит,
              что настройка не сработала.
            */}
            <span className="sub" style={{ marginBottom: 2 }}>
              {t(UI.fontScope)}
            </span>
            <div className="font-list">
              {FONTS.map((f) => (
                <button
                  key={f.id}
                  type="button"
                  className="font-row"
                  aria-selected={chrome.font === f.id}
                  onClick={() => chrome.setFont(f.id)}
                >
                  {/* Название набрано самим шрифтом: выбирать шрифт по его
                      названию в чужом шрифте — гадание. */}
                  <span className="name" style={{ fontFamily: f.stack }}>
                    {f.label}
                  </span>
                  <span className="sample" style={{ fontFamily: f.stack }}>
                    Il1 · 1,295 leads · 8.2%
                  </span>
                  <span className="why">{f.note}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="appearance-group">
            <button type="button" className="btn" onClick={() => window.print()}>
              <Icon name="print" size={14} />
              {t(UI.printScreen)}
            </button>
          </div>

          <FxRates isAdmin={isAdmin} />
        </div>
      )}
    </div>
  );
}


/* --------------------------------------------------------------- курсы валют
 *
 * Стоит в панели кастомизации по просьбе заказчика — там же, где тема и шрифт.
 * Но это НЕ оформление, и разница отмечена явно: оформление живёт в браузере
 * одного человека, а курс меняет каждую цифру по Бахрейну у всех сразу и за
 * всю историю. Поэтому у блока своя шапка, своё предупреждение и право
 * администратора; тому, у кого права нет, курс виден, но не правится — знать,
 * по какому курсу пересчитана выручка, вправе каждый, кто эту выручку видит.
 */

/** Курс печатается коротко: восемь знаков нужны арифметике, а не глазу. */
function shortRate(v: string): string {
  const n = Number(v);
  if (!Number.isFinite(n)) return v;
  return n.toFixed(4).replace(/\.?0+$/, "");
}

function FxRates({ isAdmin }: { isAdmin: boolean }): React.ReactElement {
  const [rates, setRates] = useState<FxRate[] | null>(null);
  const [changes, setChanges] = useState<FxChange[]>([]);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ kind: "ok" | "err"; text: string } | null>(null);

  useEffect(() => {
    let cancelled = false;
    void api
      .fx()
      .then((r) => {
        if (cancelled) return;
        setRates(r.rates);
        setChanges(r.changes);
        setDraft(Object.fromEntries(r.rates.map((x) => [x.currency, x.aed_per_unit])));
      })
      .catch((e: unknown) => {
        if (!cancelled) setMsg({ kind: "err", text: (e as Error).message });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const apply = async (currency: string): Promise<void> => {
    setBusy(currency);
    setMsg(null);
    try {
      const r = await api.setFxRate(currency, draft[currency] ?? "");
      if (!r.changed) {
        setMsg({ kind: "ok", text: t(UI.sameValue) });
      } else {
        setMsg({
          kind: "ok",
          text:
            `1 ${currency} = ${shortRate(r.rate.aed_per_unit)} AED ` +
            `(${t(UI.wasRate)} ${shortRate(r.from ?? "")}). ` +
            `${num(r.rowsRepriced)} ${t(UI.amountsRecalculated)}`,
        });
      }
      const fresh = await api.fx();
      setRates(fresh.rates);
      setChanges(fresh.changes);
      setDraft(Object.fromEntries(fresh.rates.map((x) => [x.currency, x.aed_per_unit])));
    } catch (e) {
      setMsg({ kind: "err", text: (e as Error).message });
    } finally {
      setBusy(null);
    }
  };

  if (rates === null) return <div className="appearance-group fx-group" />;

  const base = rates.find((r) => !r.editable);
  const editable = rates.filter((r) => r.editable);
  const last = changes[0];

  return (
    <div className="appearance-group fx-group">
      <span className="filter-label">{t(UI.exchangeRates)}</span>
      <span className="sub">{t(UI.exchangeRatesScope)}</span>

      {editable.map((r) => {
        /* Расхождение таблицы и кэша процесса. Показано не для полноты: до
           миграции 059 курс жил в двух местах, и если они снова разойдутся,
           это должно быть видно здесь, а не в расследовании причин. */
        const diverged =
          r.in_process !== null && Math.abs(r.in_process - Number(r.aed_per_unit)) > 1e-8;
        return (
          <div key={r.currency} className="fx-row">
            <div className="fx-head">
              <b>{r.currency}</b>
              <span className="fx-name">{dbLabel(CURRENCY, r.currency, r.name)}</span>
              <span className="fx-now">
                1 {r.currency} = {shortRate(r.aed_per_unit)} {base?.currency ?? "AED"}
              </span>
            </div>

            {isAdmin ? (
              <div className="fx-edit">
                <input
                  type="text"
                  inputMode="decimal"
                  value={draft[r.currency] ?? ""}
                  aria-label={`${r.currency} per ${base?.currency ?? "AED"}`}
                  onChange={(e) => setDraft((d) => ({ ...d, [r.currency]: e.target.value }))}
                />
                <button
                  type="button"
                  className="btn"
                  disabled={busy !== null || draft[r.currency] === r.aed_per_unit}
                  onClick={() => void apply(r.currency)}
                >
                  {busy === r.currency ? t(UI.recalculating) : t(UI.apply)}
                </button>
              </div>
            ) : (
              <span className="sub">{t(UI.adminCanChange)}</span>
            )}

            {diverged && (
              <span className="fx-warn">
                {t(UI.loaderStillAt)} {r.in_process} {t(UI.restartToAgree)}
              </span>
            )}
          </div>
        );
      })}

      {msg !== null && <span className={msg.kind === "ok" ? "fx-ok" : "fx-warn"}>{msg.text}</span>}

      {last !== undefined && (
        <span className="sub">
          {t(UI.lastChange)}: {shortRate(last.old_rate)} → {shortRate(last.new_rate)}{" "}
          {last.currency}, {last.changed_at.slice(0, 16).replace("T", " ")} ·{" "}
          {last.changed_by}
        </span>
      )}
    </div>
  );
}

/* ------------------------------------------------------------ заголовок экрана
 *
 * В дизайне заголовок стоит не в панели фильтров, а над содержимым: цветной
 * знак, витринный шрифт, подзаголовок в одну мысль. Цвет знака закреплён за
 * экраном — он работает как метка места, по которой видно, где ты, ещё до
 * чтения слова.
 */

const SCREEN_TINT: Record<string, string> = {
  digest: "var(--yellow)",
  overview: "var(--sky)",
  parks: "var(--yellow)",
  brands: "var(--pink)",
  pipeline: "var(--violet)",
  operations: "var(--orange)",
  marketing: "var(--sky)",
  sales: "var(--yellow)",
  journey: "var(--violet)",
  client_base: "var(--pink)",
  metrics: "var(--sand)",
  export: "var(--steel)",
  admin: "var(--steel)",
};

const SCREEN_ICON: Record<string, string> = {
  digest: "digest",
  overview: "overview",
  parks: "parks",
  brands: "brands",
  pipeline: "pipeline",
  operations: "deals",
  marketing: "marketing",
  sales: "sales",
  journey: "journey",
  client_base: "clients",
  metrics: "metrics",
  export: "export",
  admin: "admin",
};

export function ScreenHead({
  screen,
  title,
  sub,
}: {
  screen: string;
  title: string;
  sub: React.ReactNode;
}): React.ReactElement {
  return (
    <div className="screen-head">
      <span className="mark" style={{ background: SCREEN_TINT[screen] ?? "var(--sky)" }}>
        <Icon name={SCREEN_ICON[screen] ?? "overview"} size={23} width={2.4} />
      </span>
      <div className="txt">
        <h1>{title}</h1>
        <p>{sub}</p>
      </div>
    </div>
  );
}
